import { del, executeApp, get, post, put } from '@/utils/request'
import { VIEW_USER_CREATE } from '@/api/defineRouting'

// Types
export interface BillingConfigPayload {
  service_name?: string
  api_path: string
  http_method: string
  config_data: {
    billing_enabled: boolean
    required_credit_type: string
    fallback_credit_type?: string
    billing_unit: string
    base_credit_cost: number
    advance_credit_cost: number
    credit_per_token: number
    credit_per_mb: number
    minimum_credit_cost: number
    rate_limit?: number | null
    burst_limit?: number | null
    target_type?: string | null
    target_key?: string | null
    description?: string
    tags?: string[]
    tier_config?: string | null
    code_config?: string | null
  }
}

export interface BillingConfigResponse {
  id: number
  service_name: string
  api_path: string
  http_method: string
  billing_enabled: boolean
  required_credit_type: string
  fallback_credit_type?: string
  billing_unit: string
  base_credit_cost: number
  advance_credit_cost: number
  credit_per_token: number
  credit_per_mb: number
  minimum_credit_cost: number
  rate_limit?: number
  burst_limit?: number
  target_type?: string
  target_key?: string
  description?: string
  tags?: string[]
  tier_config?: string
  code_config?: string
  created_at: string
  updated_at: string
}

export interface BillingConfigListResponse {
  data: BillingConfigResponse[]
  total: number
  page: number
  page_size: number
}

// Actual API response structure from server
export interface BillingConfigApiResponse {
  id: number
  api_path: string
  http_method: string
  config: {
    billing_enabled: boolean
    required_credit_type: string
    fallback_credit_type?: string
    billing_unit: string
    base_credit_cost: number
    advance_credit_cost: number
    credit_per_token: number
    credit_per_mb: number
    minimum_credit_cost: number
    rate_limit?: number
    burst_limit?: number
    target_type?: string
    target_key?: string
    description?: string
    tags?: string[]
    tier_config?: string
    code_config?: string
  }
  created_at: string
  updated_at: string
  is_deleted: boolean
}

// API functions
export const createBillingConfig = (data: BillingConfigPayload) => {
  return post<any>({
    url: '/billing-service/api/v1/api_billing_config/create',
    data,
  })
}

export const updateBillingConfig = (id: number, data: Partial<BillingConfigPayload>) => {
  return put<BillingConfigResponse>({
    url: `/billing-service/api/v1/api_billing_config/update/${id}`,
    data,
  })
}

export const deleteBillingConfig = (id: number) => {
  return del<{ message: string }>({
    url: `/billing-service/api/v1/api_billing_config/delete/${id}`,
  })
}

export function getBillingForUser<T = any>() {
  return get<T>({
    url: '/billing-service/api/v1/view_user_credit/user_credit_view',
  })
}

export function getListBillingConfig(data: { billing_unit?: string; target_type?: string; service_name?: string; code_config?: string }) {
  return post<{ data: BillingConfigApiResponse[] }>({
    url: '/billing-service/api/v1/api_billing_config/get_list',
    data,
  })
}

// Model Pricing APIs
export function getModelPricingList(params?: { provider?: string; credit_type?: string; include_inactive?: boolean; page?: number; page_size?: number }) {
  return get<{ data: BillingConfigApiResponse[] }>({
    url: '/billing-service/api/v1/admin/model-pricing',
    data: params,
  })
}

export function createModelPricing(data: BillingConfigPayload) {
  return post<any>({
    url: '/billing-service/api/v1/admin/model-pricing/create',
    data,
  })
}

export function updateModelPricing(modelCode: string, data: Partial<BillingConfigPayload>) {
  return put<BillingConfigResponse>({
    url: `/billing-service/api/v1/admin/model-pricing/${modelCode}`,
    data,
  })
}

export function deleteModelPricing(modelCode: string) {
  return del<{ message: string }>({
    url: `/billing-service/api/v1/admin/model-pricing/delete/${modelCode}`,
  })
}

export function getModelPricingById(modelCode: string) {
  return get<BillingConfigResponse>({
    url: `/billing-service/api/v1/admin/model-pricing/${modelCode}`,
  })
}

export interface CreditExchangeRateResponse {
  base_credit_rate: number
  advance_credit_rate: number
}

export function getCreditExchangeRate() {
  return get<CreditExchangeRateResponse>({
    url: '/billing-service/api/v1/admin/model-pricing/get-active/credit-exchange-rates',
  })
}

export function updateCreditExchangeRate(data: CreditExchangeRateResponse) {
  return put<any>({
    url: '/billing-service/api/v1/admin/model-pricing/update-credit-exchange-rate',
    data,
  })
}

export function updateMarkupFactor(id: number, markup_factor: number) {
  return put<any>({
    url: `/billing-service/api/v1/admin/model-pricing/${id}/markup-factor`,
    data: { markup_factor },
  })
}

// Transaction APIs (placeholder)
export function getTransactionList(data?: any) {
  return get<any>({
    url: '/billing-service/api/v1/admin/transactions',
    data,
  })
}

export function exportTransactions(data?: any) {
  return post<any>({
    url: '/billing-service/api/v1/admin/transactions/export',
    data,
  })
}

export function getTransactionById(id: number) {
  return get<any>({
    url: `/billing-service/api/v1/admin/transactions/${id}`,
  })
}

export function getBillingConfigById<T = any>(id: number) {
  return get<T>({
    url: `/billing-service/api/v1/api_billing_config/get_detail/${id}`,
  })
}

export function getListUserBilling<T = any>(data: any) {
  return executeApp<T>(VIEW_USER_CREATE.LIST_USER_BILLING, data, 'post',
  )
}

export function updateUserCreate<T = any>(data: any) {
  return executeApp<T>(VIEW_USER_CREATE.UPDATE_USER_CREDIT, data, 'post',
  )
}

export function updateWorkSpaceCreate<T = any>(data: any) {
  return executeApp<T>(VIEW_USER_CREATE.UPDATE_WORKSPACE_CREDIT, data, 'post',
  )
}
